import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  postStockTransfer,
  saveStockTransfer,
} from '@/renderer/services/inventoryStockTransferService';
import type { SaveStockTransferRequest } from '@/renderer/types/inventory';
import { apiError, apiSuccess } from '@/renderer/util/notify';

export function useSaveStockTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SaveStockTransferRequest) =>
      saveStockTransfer(request),
    onSuccess: (result, variables) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to save transfer');
        return;
      }
      apiSuccess(variables.id ? 'Transfer updated' : 'Transfer created');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-transfers-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-transfer-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to save transfer');
    },
  });
}

export function usePostStockTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transferId: string) => postStockTransfer(transferId),
    onSuccess: (result) => {
      if (!result.success) {
        apiError(result.error ?? 'Failed to post transfer');
        return;
      }
      apiSuccess('Transfer posted — stock balances updated');
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-transfers-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-transfer-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-balances-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-balance-summary'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-movements-list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['inventory', 'stock-movement-summary'],
      });
    },
    onError: (error: Error) => {
      apiError(error.message ?? 'Failed to post transfer');
    },
  });
}
